<template>
  <div v-if="featureEnabled && (!showSnapshotText || currentAiSnapshot)" class="ai-scorecard-bar"
    :class="[`ai-scorecard-bar--${mode}`, {
      'ai-scorecard-bar--pinned': isPinned,
      'ai-scorecard-bar--with-away-message': hasAwayMessage,
      'ai-scorecard-bar--text': showSnapshotText
    }]">
    <span v-if="aiUnavailable" class="ai-scorecard-bar__unavailable">
      <i class="mdi mdi-information-outline"></i>
      <span>AI Not Available:
        <router-link :to="{ name: 'hospital.video_devices.edit', params: { id: videoDevice?.id } }" target="_blank">Check Device</router-link>
      </span>
    </span>
    <div v-else-if="showSnapshotText" class="ai-scorecard-bar__text-content">
      <div class="ai-scorecard-bar__snapshot" v-tooltip="{ content: currentAiSnapshot, theme: 'lookdeep-global' }">
        {{ currentAiSnapshot }}
      </div>
      <div v-if="currentUpdatedAt" class="is-flex is-align-items-center mt-1" style="opacity: 0.7; font-size: 12px;">
        <button class="ai-scorecard-bar__refresh" @click="refreshSnapshot" :disabled="isRefreshing">
          <b-loading v-if="isRefreshing" :is-full-page="false" v-model="isRefreshing" :can-cancel="false"></b-loading>
          <i v-else class="mdi mdi-refresh"></i>
        </button>
        <span v-tooltip="$helpers.formatDate(currentUpdatedAt)">Updated {{ $helpers.getHumanTimeDifference(currentUpdatedAt) }}</span>
      </div>
    </div>
    <template v-else>
      <div class="is-flex is-flex-direction-column" style="width: 100%">
        <div style="display: flex;justify-content: center;gap: 15px;">
          <crystal-ball :patient-id="patientId" :patient-monitor="patientMonitor" :ai-snapshot="aiSnapshot" :ai-snapshot-updated-at="aiSnapshotUpdatedAt" style="width: 24px;" />
          <span v-for="chip in chips" :key="chip.field.key" v-tooltip="{ content: chip.tooltip, placement: 'bottom' }" class="ai-scorecard-bar__chip" :class="`ai-scorecard-bar__chip--${chip.state}`">
            <i class="mdi" :class="chip.icon"></i>
          </span>
        </div>
        <div>
          <span v-if="mode !== 'video-overlay' && currentUpdatedAt" class="ai-scorecard-bar__updated">
            <button class="ai-scorecard-bar__refresh" @click="refreshSnapshot" :disabled="isRefreshing">
              <b-loading v-if="isRefreshing" :is-full-page="false" v-model="isRefreshing" :can-cancel="false"></b-loading>
              <i v-else class="mdi mdi-refresh"></i>
            </button>
            <span v-tooltip="$helpers.formatDate(currentUpdatedAt)">Updated {{ $helpers.getHumanTimeDifference(currentUpdatedAt) }}</span>
          </span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { CHANNEL, SERVER_EVENT } from '@/lib/Pusher'
import { resolveAiScorecard } from '@/lib/PatientUtils'
import CrystalBall from '@/components/CrystalBall.vue'
import AiSnapshotRefreshMixin from '@/components/mixins/AiSnapshotRefreshMixin'

export default {
  name: 'AiScorecardBar',
  components: { CrystalBall },
  mixins: [AiSnapshotRefreshMixin],
  props: {
    scorecard: { type: Array, default: () => [] },
    aiSnapshot: { type: String, default: null },
    aiSnapshotUpdatedAt: { type: String, default: null },
    patientId: { type: Number, required: true },
    patientMonitor: { type: Object, default: null },
    patient: { type: Object, default: null },
    blindsClosed: { type: Boolean, default: false },
    hasAwayMessage: { type: Boolean, default: false },
    mode: {
      type: String,
      default: 'video-overlay',
      validator: v => ['video-overlay', 'modal', 'inline'].includes(v)
    }
  },
  data() {
    return {
      hospitalChannel: null,
      localScorecard: null,
      localAiSnapshot: null,
      localAiSnapshotUpdatedAt: null
    }
  },
  computed: {
    refreshMonitorId() {
      return this.patientMonitor?.id
    },
    aiUnavailable() {
      return this.videoDevice?.state?.ai_status === 'unhealthy'
    },
    currentUpdatedAt() {
      return this.localAiSnapshotUpdatedAt || this.aiSnapshotUpdatedAt
    },
    featureEnabled() {
      return this.$store.state.currentUser?.enabled_flags?.includes('ai_scorecard_display')
    },
    chips() {
      return resolveAiScorecard(this.localScorecard || this.scorecard).filter(r => r.state !== 'unknown')
    },
    isPinned() {
      return this.mode !== 'video-overlay' || this.blindsClosed
    },
    nurseStationEnabled() {
      return this.$store.state.CentralMonitoringStore?.nurseStationEnabled
    },
    showSnapshotText() {
      return this.mode === 'video-overlay' && this.blindsClosed && !this.nurseStationEnabled
    },
    currentAiSnapshot() {
      return this.localAiSnapshot || this.aiSnapshot
    },
    videoDevice() {
      return (this.patient?.patient_monitor || this.patientMonitor)?.video_device
    }
  },
  methods: {
    handleAiSnapshotUpdated(data) {
      if (data.patient_id === this.patientId) {
        this.localScorecard = data.ai_snapshot_scorecard
        this.localAiSnapshot = data.ai_snapshot
        this.localAiSnapshotUpdatedAt = data.ai_snapshot_updated_at
        this.completeSnapshotRefresh()
      }
    }
  },
  mounted() {
    this.hospitalChannel = this.$pusher.subscribe(CHANNEL.FOR_HOSPITAL({
      env: import.meta.env.VITE_ENVIRONMENT_NAME,
      hospital: this.$store.state.currentHospital
    }))
    this.hospitalChannel.bind(SERVER_EVENT.AI_SNAPSHOT_UPDATED, this.handleAiSnapshotUpdated)
  },
  beforeUnmount() {
    this.hospitalChannel?.unbind(SERVER_EVENT.AI_SNAPSHOT_UPDATED, this.handleAiSnapshotUpdated)
  }
}
</script>

<style scoped lang="scss">
.ai-scorecard-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  padding: 6px 12px;
  background: rgb(0 0 0 / 36%);
  color: var(--ld-text-inverse);
  border-radius: 4px;
  font-size: 16px;
  line-height: 1;
  pointer-events: auto;
}

.ai-scorecard-bar--video-overlay {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  z-index: 20;
  opacity: 0;
  transition: opacity 0.1s linear, top 0.1s linear;
}

.ai-scorecard-bar--pinned {
  opacity: 1;
}

.ai-scorecard-bar--video-overlay.ai-scorecard-bar--with-away-message {
  top: 26px;
}

.ai-scorecard-bar--modal {
  position: static;
  justify-content: center;
  margin: 0 0 12px 0;
  background: var(--ld-bg-tertiary);
  border-radius: 0;
  color: inherit;
}

.ai-scorecard-bar--inline {
  position: static;
  padding: 4px 8px;
  gap: 8px;
  background: transparent;
  color: inherit;
  font-size: 14px;
}

.ai-scorecard-bar__chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;

  i {
    font-size: 26px;
  }
}

.ai-scorecard-bar__chip--active {
  color: var(--ld-status-success);
}
.ai-scorecard-bar__chip--inactive {
  color: var(--ld-text-secondary);
}

.ai-scorecard-bar--text {
  justify-content: flex-start;
  text-align: left;
}

.ai-scorecard-bar__updated {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  opacity: 0.8;
  justify-content: center;
  margin-top: 3px
}

.ai-scorecard-bar__refresh {
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
  padding: 0;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  i {
    font-size: 16px;
  }
  .loading-overlay {
    position: static;
    background: none !important;
    :deep(.loading-background) {
      display: none;
    }
    :deep(.loading-icon::after) {
      position: static;
      width: 14px;
      height: 14px;
      border-width: 2px;
    }
  }
}

.ai-scorecard-bar__unavailable {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 12px;
  line-height: 1.2;
  white-space: nowrap;
  color: var(--ld-status-danger);
  background: color-mix(in srgb, currentColor 12%, transparent);

  i {
    font-size: 14px;
  }

  a {
    color: inherit;
    text-decoration: underline;
  }
}

.ai-scorecard-bar__text-content {
  width: 100%;
}

.ai-scorecard-bar__snapshot {
  margin: 0;
  padding-right: 20px;
  font-size: 13px;
  line-height: 1.3;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
